import React, { useRef, useContext, useState, useCallback } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBBadge,
  MDBSpinner
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useFileUpload } from '../hooks/useFileUpload';
import { DevContext } from '../../../../App';
import { captureError } from '../../../../utils/sentryHelpers';

export default function PresentationStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);
  const fileInputRef = useRef(null);
  
  // Use the file upload hook
  const { 
    uploadLectureFile, 
    removeFile,
    getFileUrl,
    getFileIcon,
    getFileColor,
    isUploading, 
    uploadError: hookUploadError,
    resetUploadState 
  } = useFileUpload();
  
  const [localUploadError, setLocalUploadError] = useState(null);

  // Get validation info from schema
  const stepValidation = getStepValidation('presentation');
  const requiredFields = getStepRequiredFields('presentation');
  const presentationFieldValidation = getFieldValidation('presentation');

  const processFile = async (file) => {
    setLocalUploadError(null);
    resetUploadState(); // Reset hook errors before upload

    // Upload using the hook - errors are already handled and logged in the hook
    const uploadedFile = await uploadLectureFile(file, 'presentation');

    if (uploadedFile) {
      // Update form data with uploaded file info
      handleFieldChange('presentation', uploadedFile);
    }
    // If uploadedFile is null, the error is already set in hookUploadError by the hook
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemoveFile = async () => {
    try {
      if (formData.presentation?.fileName) {
        await removeFile(formData.presentation.fileName, 'protected');
      }
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'remove-presentation',
        consoleMessage: 'Error removing file:',
        context: { fileName: formData.presentation?.fileName }
      });
    }
    
    handleFieldChange('presentation', null);
    setLocalUploadError(null);
    resetUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadFile = async () => {
    try {
      const url = await getFileUrl(formData.presentation.fileName, 'protected');
      if (url) {
        window.open(url, '_blank');
      }
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'download-presentation',
        consoleMessage: 'Error downloading file:',
        context: { fileName: formData.presentation?.fileName }
      });
    }
  };
  
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0]);
    }
  }, []);

  const onDropRejected = useCallback((fileRejections) => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      const errors = rejection.errors;

      if (errors.some(e => e.code === 'file-too-large')) {
        setLocalUploadError(t('File size must be less than 50MB'));
      } else {
        setLocalUploadError(t('Invalid file. Please try again.'));
      }
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: false,
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const handleContinue = () => {
    if (stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const handleSkip = () => {
    // Only allow skip if presentation is not required
    if (!requiredFields.includes('presentation') || stepValidation.isValid) {
      handleFieldChange('presentation', null);
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = stepValidation.isValid;
  const canSkip = !requiredFields.includes('presentation') || stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Presentation')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {t(`Upload your presentation here, which will be shown during your talk. You don't need to bring it yourself.
              The presentation slides will be used for recording the presentations and will not be shared or published.
              PPT/PPTX (PowerPoint) or PDF formats are preferred, but all file formats are accepted.`)}<br />
            {eventConfig?.lecturePresentationEndDate && ['vip', 'technical', 'educational'].includes(formData.type) && (
              <>
                {t('The presentation must be uploaded by {{date}}.', {
                  date: new Date(eventConfig.lecturePresentationEndDate).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    month: 'long', 
                    year: 'numeric'
                  })
                })}
              </>
            )}
          </MDBTypography>
          
          {!formData.presentation ? (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <MDBCard 
                className={`text-center p-4 cursor-pointer ${
                  isDragActive ? 'border-primary' : 'border-dashed'
                }`}
                style={{ 
                  cursor: 'pointer',
                  borderStyle: 'dashed',
                  borderWidth: '2px',
                  borderColor: isDragActive ? '#0d6efd' : '#dee2e6',
                  backgroundColor: isDragActive ? '#f8f9fa' : 'transparent'
                }}
              >
                <MDBCardBody>
                  {isUploading ? (
                    <>
                      <MDBSpinner role="status" className="mb-3" />
                      <MDBTypography tag="h6" className="mb-2">
                        {t('Uploading presentation...')}
                      </MDBTypography>
                    </>
                  ) : (
                    <>
                      <MDBIcon 
                        icon="file-powerpoint" 
                        size="3x" 
                        className="text-muted mb-3"
                      />
                      <MDBTypography tag="h6" className="mb-2">
                        {isDragActive ? t('Drop file here') : t('Upload Presentation')}
                      </MDBTypography>
                      <MDBTypography tag="p" className="text-muted small mb-3">
                        {t('Drag and drop a file or click to select')}
                      </MDBTypography>
                      <MDBTypography tag="small" className="text-muted">
                        <strong>{t('PowerPoint format (PPT/PPTX) preferred')}</strong><br/>
                        {t('All file formats accepted (max 50MB)')}
                      </MDBTypography>
                    </>
                  )}
                </MDBCardBody>
              </MDBCard>
            </div>
          ) : (
            <MDBCard>
              <MDBCardBody>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <MDBIcon 
                      icon={getFileIcon(formData.presentation.fileName)} 
                      className={`${getFileColor(formData.presentation.fileName)} me-3`} 
                      size="2x"
                    />
                    <div>
                      <MDBTypography tag="h6" className="mb-1">
                        {formData.presentation.file?.name || formData.presentation.fileName || 'Uploaded File'}
                      </MDBTypography>
                      {formData.presentation.file?.size && (
                        <MDBTypography tag="small" className="text-muted">
                          {(formData.presentation.file.size / 1024 / 1024).toFixed(2)} MB
                        </MDBTypography>
                      )}
                      <div className="mt-2">
                        <MDBBtn
                          color="link"
                          size="sm"
                          onClick={downloadFile}
                          className="p-0 me-3"
                        >
                          <MDBIcon icon="download" className="me-1" />
                          {t('Download')}
                        </MDBBtn>
                      </div>
                    </div>
                  </div>
                  <MDBBtn
                    color="danger"
                    size="sm"
                    onClick={handleRemoveFile}
                    disabled={isUploading}
                  >
                    <MDBIcon icon="trash" />
                  </MDBBtn>
                </div>
              </MDBCardBody>
            </MDBCard>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </MDBCol>
      </MDBRow>

      {(hasFieldError('presentation') || localUploadError || hookUploadError) && (
        <MDBRow className="mb-3">
          <MDBCol>
            <div className="alert alert-danger" role="alert">
              <MDBIcon icon="exclamation-triangle" className="me-2" />
              {localUploadError || hookUploadError || getFieldError('presentation')}
            </div>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Presentation Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Is Presentation Required:</strong>{' '}
                <MDBBadge color={requiredFields.includes('presentation') ? 'warning' : 'info'}>
                  {requiredFields.includes('presentation') ? 'Required by Schema' : 'Optional'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Current Presentation:</strong>{' '}
                {formData.presentation ? (
                  <>File: {formData.presentation.file?.name || formData.presentation.fileName || 'Uploaded File'} 
                  {formData.presentation.file?.size && `(${(formData.presentation.file.size / 1024 / 1024).toFixed(2)} MB)`}</>
                ) : (
                  'No presentation selected'
                )}
              </div>
              
              <div className="mb-2">
                <strong>Upload Status:</strong>{' '}
                <MDBBadge color={isUploading ? 'info' : (localUploadError || hookUploadError) ? 'danger' : 'success'}>
                  {isUploading ? 'Uploading...' : (localUploadError || hookUploadError) ? 'Error' : 'Ready'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {presentationFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{presentationFieldValidation.field}</code>: {presentationFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Skip Button:</strong>{' '}
                <MDBBadge color={canSkip ? 'success' : 'warning'}>
                  {canSkip ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={canContinue ? 'success' : 'warning'}>
                  {canContinue ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <div>
                <MDBBtn 
                  color="light"
                  onClick={handleSkip}
                  className="me-2"
                  disabled={!canSkip}
                >
                  {t('Skip')}
                </MDBBtn>
                <MDBBtn 
                  onClick={handleContinue}
                  disabled={!canContinue}
                >
                  {t('Continue')}
                </MDBBtn>
              </div>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}